import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { JobGalleries } from "./job/JobGalleries";
import { SponsorPlacementsPanel } from "./SponsorPlacementsPanel";
import { ProjectHeader, EmailCoverCard, WebsiteUrlCard, PasscodeCard, ListingDetailsCard, ShowingRequestsCard, WebsiteTabHints } from "./GalleryProjectDialogSections";

const tabCls = "rounded-lg data-[state=active]:bg-[#0C2347] data-[state=active]:text-white font-mono text-[11px] uppercase tracking-[0.1em] text-[#475467]";

const DETAIL_KEYS = ["price", "beds", "baths", "sqft", "listing_status", "mls_number", "property_type", "lot_size", "year_built", "stories", "parking", "heating_cooling", "taxes", "hoa_fee", "hoa_period", "website_description"];

function detailsFromDelivery(d) {
  return Object.fromEntries(DETAIL_KEYS.map((key) => [key, d[key] || ""]));
}

export function GalleryProjectDialog({ bookingId, onClose, onChanged, onSend }) {
  const [job, setJob] = useState(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ project_name: "" });
  const [details, setDetails] = useState(() => detailsFromDelivery({}));
  const [savedDetails, setSavedDetails] = useState(() => detailsFromDelivery({}));
  const [highlights, setHighlights] = useState([]);
  const [highlightDraft, setHighlightDraft] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsJustSaved, setDetailsJustSaved] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const qrRef = useRef(null);
  const justSavedTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(justSavedTimeoutRef.current), []);

  const savedDetailsRef = useRef(detailsFromDelivery({}));

  const reload = useCallback(() => api.get(`/admin/jobs/${bookingId}`).then((r) => {
    setJob(r.data);
    const d = r.data.delivery || {};
    const fresh = detailsFromDelivery(d);
    const prevSaved = savedDetailsRef.current;
    setDetails((prevDetails) => {
      const merged = { ...fresh };
      for (const key of DETAIL_KEYS) {
        if (prevDetails[key] !== prevSaved[key]) merged[key] = prevDetails[key];
      }
      return merged;
    });
    savedDetailsRef.current = fresh;
    setSavedDetails(fresh);
    setHighlights(d.highlights || []);
  }).catch(() => {}), [bookingId]);
  useEffect(() => { reload(); }, [reload]);
  const changed = () => { reload(); onChanged(); };

  if (!job) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-xl p-10" data-testid="gallery-project-dialog-loading">
        <DialogTitle className="sr-only">Loading project</DialogTitle><DialogDescription className="sr-only">Loading</DialogDescription>
        <div className="text-sm text-[#94A3B8] text-center py-10">Loading project…</div>
      </DialogContent>
    </Dialog>
  );

  const delivery = job.delivery || {};
  const clients = [job.primary_client, ...(job.additional_clients || [])].filter(Boolean);
  const listingUrl = delivery.gallery_slug ? `${window.location.origin}/listing/${delivery.gallery_slug}` : null;

  const patchDelivery = async (value, successMsg) => {
    try { await api.patch(`/admin/orders/${job.id}/delivery`, value); if (successMsg) toast.success(successMsg); changed(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const copy = async (url, label) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Couldn't copy ${label.toLowerCase()}`);
    }
  };
  const saveHeader = async () => {
    try { await api.patch(`/admin/orders/${job.id}/delivery`, { project_name: headerDraft.project_name.trim() }); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setEditingHeader(false); changed();
  };
  const updateClients = async (nextAdditionalIds) => {
    try { await api.patch(`/admin/jobs/${job.id}`, { additional_client_ids: nextAdditionalIds }); toast.success("Clients updated"); changed(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const reassignClient = async (client) => {
    const currentName = job.primary_client?.name || job.contact?.name || "this project";
    if (!window.confirm(`Reassign this project from "${currentName}" to "${client.name}"? Future delivery emails will go to ${client.name} instead.`)) return;
    setReassigning(true);
    try {
      await api.post(`/admin/jobs/${job.id}/reassign-client`, { client_id: client.id });
      toast.success(`Project reassigned to ${client.name}`);
      setReassignOpen(false);
      changed();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setReassigning(false); }
  };
  const saveDetails = async (overrides = {}) => {
    const next = { ...details, ...overrides };
    setSavingDetails(true);
    try {
      await api.patch(`/admin/orders/${job.id}/delivery`, next);
      const cleaned = detailsFromDelivery(next);
      setDetails(cleaned);
      setSavedDetails(cleaned);
      savedDetailsRef.current = cleaned;
      setDetailsJustSaved(true);
      clearTimeout(justSavedTimeoutRef.current);
      justSavedTimeoutRef.current = setTimeout(() => setDetailsJustSaved(false), 2500);
      changed();
      return true;
    }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); return false; }
    finally { setSavingDetails(false); }
  };
  const saveHighlights = async (next) => {
    setHighlights(next);
    try { await api.patch(`/admin/orders/${job.id}/delivery`, { highlights: next }); changed(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const addHighlight = () => {
    const value = highlightDraft.trim();
    if (!value) return;
    saveHighlights([...highlights, value]);
    setHighlightDraft("");
  };
  const extractFromZillow = async ({ text, file }) => {
    try {
      const form = new FormData();
      if (file) form.append("image", file); else form.append("text", text);
      const { data } = await api.post(`/admin/orders/${job.id}/listing-details/extract`, form);
      const { highlights: newHighlights, ...rawFields } = data;
      const detailFields = Object.fromEntries(Object.entries(rawFields).filter(([, v]) => v));
      const mergedHighlights = [...new Set([...highlights, ...newHighlights])].slice(0, 12);
      const ok = await saveDetails({ ...detailFields, highlights: mergedHighlights });
      if (!ok) return false;
      setDetails((prev) => ({ ...prev, ...detailFields }));
      setHighlights(mergedHighlights);
      toast.success("Filled from Zillow — review and adjust as needed");
      return true;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); return false; }
  };
  const updateShowingStatus = async (requestId, status) => {
    try { await api.patch(`/admin/orders/${job.id}/showing-requests/${requestId}`, { status }); changed(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const downloadQrCode = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${job.job_number}-qr-code.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  const imageFiles = (job.files || []).filter((f) => f.is_image);
  const emailCoverId = delivery.email_cover_file_id || job.cover_file_id;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[96vw] h-[92vh] rounded-xl p-0 flex flex-col overflow-hidden [&>button.absolute]:hidden" data-testid="gallery-project-dialog">
        <DialogTitle className="sr-only">Gallery project</DialogTitle><DialogDescription className="sr-only">Manage this project's photos and property website.</DialogDescription>
        <ProjectHeader job={job} delivery={delivery} editingHeader={editingHeader} headerDraft={headerDraft} setHeaderDraft={setHeaderDraft}
          setEditingHeader={setEditingHeader} saveHeader={saveHeader} clients={clients} reassignOpen={reassignOpen} setReassignOpen={setReassignOpen}
          reassigning={reassigning} updateClients={updateClients} reassignClient={reassignClient} onSend={onSend} onClose={onClose} />
        <Tabs defaultValue="media" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 md:mx-8 mt-4 bg-[#F7F9FC] border border-[#E4E9F0] rounded-xl p-1 h-auto flex-wrap self-start">
            <TabsTrigger value="media" data-testid="gallery-project-tab-deliverables" className={tabCls}>Photos & Media</TabsTrigger>
            <TabsTrigger value="website" data-testid="gallery-project-tab-website" className={tabCls}>Property Website</TabsTrigger>
          </TabsList>
          <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 space-y-8">
            <TabsContent value="media" className="mt-0 space-y-6">
              <EmailCoverCard imageFiles={imageFiles} emailCoverId={emailCoverId} job={job} fileUrl={fileUrl} patchDelivery={patchDelivery} />
              <JobGalleries job={job} onChanged={changed} />
            </TabsContent>
            <TabsContent value="website" className="mt-0 space-y-6">
              <WebsiteUrlCard delivery={delivery} listingUrl={listingUrl} copy={copy} patchDelivery={patchDelivery} downloadQrCode={downloadQrCode} qrRef={qrRef} />
              <PasscodeCard delivery={delivery} copy={copy} />
              <ListingDetailsCard details={details} setDetails={setDetails} saveDetails={saveDetails} highlights={highlights}
                highlightDraft={highlightDraft} setHighlightDraft={setHighlightDraft} addHighlight={addHighlight}
                saveHighlights={saveHighlights} savingDetails={savingDetails} detailsDirty={JSON.stringify(details) !== JSON.stringify(savedDetails)}
                detailsJustSaved={detailsJustSaved} onExtractFromZillow={extractFromZillow} />
              <SponsorPlacementsPanel job={job} onChanged={changed} />
              <ShowingRequestsCard requests={job.delivery?.showing_requests || []} updateShowingStatus={updateShowingStatus} />
              <WebsiteTabHints />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
